import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormGroup,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import { connect } from 'react-redux';
import LoaderWidget from './Loader';
import { Dropdown } from './Dropdown';
import {
  prepareForDowngrade,
  getUserOwnedVPs,
  bulkUpdateVPKeepStatus,
} from '../redux/valuePropositions/actions';
import { Plan, getVPLimit } from '../helpers/plan';
import { formatMoney, getCurrencyByIndex } from '../helpers/money';
import { getCurrencies } from '../redux/actions';

const SortingOptions = {
  MOST_RECENT: { label: 'Most Recent' },
  LEAST_RECENT: { label: 'Oldest' },
  VP_NAME_ASC: { label: 'Name (A-Z)' },
  VP_NAME_DESC: { label: 'Name (Z-A)' },
  IMPLEMENTATION_DESC: { label: 'Delivered Value', rightIcon: <i className="uil uil-arrow-down" /> },
  IMPLEMENTATION_ASC: { label: 'Delivered Value', rightIcon: <i className="uil uil-arrow-up" /> },
};

const getVpDateTimestamp = (vp) => {
  const date = vp.updateDate || vp.UpdateDate || vp.createdDate || vp.CreatedDate;
  return date ? new Date(date).getTime() : 0;
};

const getVpName = (vp) => vp.vpName || vp.VpName || '';

const getVpImplementation = (vp) => {
  const value = vp.implementation ?? vp.Implementation;
  return value == null ? null : value;
};

const sortOwnedVps = (a, b, orderedByOption) => {
  switch (orderedByOption) {
    case SortingOptions.LEAST_RECENT:
      return getVpDateTimestamp(a) - getVpDateTimestamp(b);
    case SortingOptions.VP_NAME_ASC:
      return getVpName(a).localeCompare(getVpName(b));
    case SortingOptions.VP_NAME_DESC:
      return getVpName(b).localeCompare(getVpName(a));
    case SortingOptions.IMPLEMENTATION_DESC: {
      const aVal = getVpImplementation(a);
      const bVal = getVpImplementation(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return -1;
      if (bVal === null) return 1;
      return bVal - aVal;
    }
    case SortingOptions.IMPLEMENTATION_ASC: {
      const aVal = getVpImplementation(a);
      const bVal = getVpImplementation(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return -1;
      if (bVal === null) return 1;
      return aVal - bVal;
    }
    case SortingOptions.MOST_RECENT:
    default:
      return getVpDateTimestamp(b) - getVpDateTimestamp(a);
  }
};

export const VPSelectionModal = ({
  isOpen,
  toggleModal,
  onConfirm,
  newPlanId,
  currentPlan,
  isUpgrade = false,
  /** Policy cap-down after login (same keep/limit API; no subscription checkout). */
  isPolicyCapDown = false,
  // Redux props
  userOwnedVPs,
  userOwnedVPsLoading,
  userOwnedVPsError,
  preparingForDowngrade,
  prepareForDowngradeError,
  updatingVPKeepStatus,
  updateVPKeepStatusError,
  // Redux actions
  prepareForDowngrade: prepareForDowngradeAction,
  getUserOwnedVPs: getUserOwnedVPsAction,
  bulkUpdateVPKeepStatus: bulkUpdateVPKeepStatusAction,
  subscriptionRulesByPlan,
  currencies,
  getCurrencies: getCurrenciesAction,
}) => {
  const [selectedVPs, setSelectedVPs] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderedByOption, setOrderedByOption] = useState(SortingOptions.MOST_RECENT);

  // Refs to track if API calls have been initiated for the current modal session
  const hasInitiatedPrepareRef = useRef(false);
  const hasInitiatedGetVPsRef = useRef(false);
  const previousIsOpenRef = useRef(false);

  const vpLimit = getVPLimit(subscriptionRulesByPlan, newPlanId);
  // Policy alignment and upgrade both load owned VPs directly (no prepareForDowngrade).
  const loadVpsDirectly = isUpgrade || isPolicyCapDown;
  // Reset refs when modal closes
  useEffect(() => {
    if (!isOpen && previousIsOpenRef.current) {
      // Modal just closed, reset the refs for next time
      hasInitiatedPrepareRef.current = false;
      hasInitiatedGetVPsRef.current = false;
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !currencies) {
      getCurrenciesAction();
    }
  }, [isOpen, currencies, getCurrenciesAction]);

  // Load VPs only when modal opens (never on Confirm - we already have the list and selection)
  useEffect(() => {
    if (!isOpen || isProcessing) return;

    if (
      !loadVpsDirectly &&
      !hasInitiatedPrepareRef.current &&
      !preparingForDowngrade &&
      !userOwnedVPsLoading
    ) {
      hasInitiatedPrepareRef.current = true;
      prepareForDowngradeAction();
      return;
    }

    if (loadVpsDirectly && !hasInitiatedGetVPsRef.current && !userOwnedVPsLoading && !userOwnedVPs) {
      hasInitiatedGetVPsRef.current = true;
      getUserOwnedVPsAction();
      return;
    }
  }, [
    isOpen,
    loadVpsDirectly,
    isProcessing,
    preparingForDowngrade,
    userOwnedVPsLoading,
    userOwnedVPs,
    prepareForDowngradeAction,
    getUserOwnedVPsAction,
  ]);

  // After preparing (for downgrades), load VPs once. Never refetch on Confirm.
  useEffect(() => {
    if (!isOpen || loadVpsDirectly || isProcessing) return;

    if (
      hasInitiatedPrepareRef.current &&
      !preparingForDowngrade &&
      !prepareForDowngradeError &&
      !hasInitiatedGetVPsRef.current &&
      !userOwnedVPsLoading &&
      !userOwnedVPs
    ) {
      hasInitiatedGetVPsRef.current = true;
      getUserOwnedVPsAction();
    }
  }, [
    isOpen,
    loadVpsDirectly,
    isProcessing,
    preparingForDowngrade,
    prepareForDowngradeError,
    userOwnedVPsLoading,
    userOwnedVPs,
    getUserOwnedVPsAction,
  ]);

  // Initialize selectedVPs when VPs are loaded (all checked by default)
  useEffect(() => {
    if (userOwnedVPs && userOwnedVPs.length > 0) {
      console.log('VPSelectionModal - userOwnedVPs loaded:', userOwnedVPs.length, 'VPs');
      console.log('VPSelectionModal - first VP sample:', userOwnedVPs[0]);
      const initialSelection = {};
      userOwnedVPs.forEach((vp) => {
        // Handle both PascalCase (from API) and camelCase
        const vpId = vp.vpId || vp.VpId;
        const isLimited =
          vp.isLimited !== undefined
            ? vp.isLimited
            : vp.IsLimited !== undefined
            ? vp.IsLimited
            : false; // Default to false (not limited)
        // If not limited, VP should be checked (selected)
        initialSelection[vpId] = !isLimited; // Inverted: IsLimited = false means checked
      });
      setSelectedVPs(initialSelection);
    } else if (userOwnedVPs && userOwnedVPs.length === 0) {
      console.log('VPSelectionModal - userOwnedVPs is empty array');
    }
  }, [userOwnedVPs]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedVPs({});
      setIsProcessing(false);
      setOrderedByOption(SortingOptions.MOST_RECENT);
      // Reset refs when modal closes (handled in separate useEffect above)
    }
  }, [isOpen]);

  const handleToggleVP = (vpId) => {
    setSelectedVPs((prev) => ({
      ...prev,
      [vpId]: !prev[vpId],
    }));
  };

  const handleSelectAll = () => {
    if (!userOwnedVPs || userOwnedVPs.length === 0) return;

    const allSelected = userOwnedVPs.every((vp) => {
      const vpId = vp.vpId || vp.VpId;
      return selectedVPs[vpId] === true;
    });

    if (allSelected) {
      // Deselect all
      setSelectedVPs({});
    } else {
      // Select all VPs (user can manually deselect if they exceed the limit)
      const newSelection = {};
      userOwnedVPs.forEach((vp) => {
        const vpId = vp.vpId || vp.VpId;
        newSelection[vpId] = true;
      });
      setSelectedVPs(newSelection);
    }
  };

  const handleSelectLatest = () => {
    if (!userOwnedVPs || userOwnedVPs.length === 0) return;

    // Sort VPs by date (most recent first)
    // Backend returns them sorted by IsLimited first, then by UpdateDate descending
    // We want to select the latest ones regardless of IsLimited status
    const sortedVPs = [...userOwnedVPs].sort((a, b) => {
      const dateA = a.updateDate || a.UpdateDate || a.createdDate || a.CreatedDate;
      const dateB = b.updateDate || b.UpdateDate || b.createdDate || b.CreatedDate;

      // Convert to timestamps for comparison
      const timestampA = dateA ? new Date(dateA).getTime() : 0;
      const timestampB = dateB ? new Date(dateB).getTime() : 0;

      // Most recent first (descending order)
      return timestampB - timestampA;
    });

    // Select only the first vpLimit VPs (most recent ones)
    const newSelection = {};
    const vpsToSelect = Math.min(vpLimit, sortedVPs.length);

    for (let i = 0; i < vpsToSelect; i++) {
      const vp = sortedVPs[i];
      const vpId = vp.vpId || vp.VpId;
      newSelection[vpId] = true;
    }

    setSelectedVPs(newSelection);
  };

  const handleConfirm = async () => {
    console.log('VPSelectionModal - handleConfirm called', {
      userOwnedVPs: userOwnedVPs?.length,
      selectedVPs,
      bulkUpdateVPKeepStatusAction,
    });

    if (!userOwnedVPs || userOwnedVPs.length === 0) {
      // No VPs to update, proceed with downgrade
      console.log('VPSelectionModal - No VPs to update, proceeding with downgrade');
      onConfirm();
      return;
    }

    setIsProcessing(true);

    // Prepare VP updates
    const vpUpdates = userOwnedVPs.map((vp) => {
      // Handle both PascalCase (from API) and camelCase
      const vpId = vp.vpId || vp.VpId;
      // If VP is checked (selectedVPs[vpId] === true), then IsLimited = false (not limited)
      // If VP is unchecked (selectedVPs[vpId] === false), then IsLimited = true (limited)
      return {
        vpId: vpId,
        isLimited: selectedVPs[vpId] !== true, // Inverted: checked = false (not limited), unchecked = true (limited)
      };
    });

    console.log('VPSelectionModal - Calling bulkUpdateVPKeepStatusAction with:', {
      vpsCount: vpUpdates.length,
      vpUpdates: vpUpdates.slice(0, 3), // Log first 3 for debugging
      actionFunction: typeof bulkUpdateVPKeepStatusAction,
      actionFunctionName: bulkUpdateVPKeepStatusAction?.name || 'unknown',
      isFunction: typeof bulkUpdateVPKeepStatusAction === 'function',
    });

    // Update VP keep status
    if (typeof bulkUpdateVPKeepStatusAction !== 'function') {
      console.error(
        'VPSelectionModal - bulkUpdateVPKeepStatusAction is not a function!',
        bulkUpdateVPKeepStatusAction
      );
      alert('Error: Action not properly connected. Please refresh the page.');
      setIsProcessing(false);
      return;
    }

    try {
      console.log('VPSelectionModal - About to dispatch bulkUpdateVPKeepStatusAction', {
        vpsCount: vpUpdates.length,
        actionFunction: typeof bulkUpdateVPKeepStatusAction,
        actionFunctionName: bulkUpdateVPKeepStatusAction?.name,
      });

      // Dispatch the action - connect wraps this with dispatch automatically
      const actionResult = bulkUpdateVPKeepStatusAction({ vps: vpUpdates });

      console.log('VPSelectionModal - Action result:', {
        type: actionResult?.type,
        payload: actionResult?.payload,
        hasType: !!actionResult?.type,
        actionType: actionResult?.type === 'BULK_UPDATE_VP_KEEP_STATUS' ? 'CORRECT' : 'WRONG TYPE!',
        fullResult: actionResult,
      });

      // Verify action was created correctly
      if (!actionResult) {
        console.error('VPSelectionModal - Action result is null/undefined!');
        setIsProcessing(false);
        alert('Error: Failed to dispatch action. Please refresh the page.');
        return;
      }

      if (actionResult.type !== 'BULK_UPDATE_VP_KEEP_STATUS') {
        console.error('VPSelectionModal - Action type mismatch!', {
          expected: 'BULK_UPDATE_VP_KEEP_STATUS',
          actual: actionResult?.type,
          actionResult,
        });
        setIsProcessing(false);
        alert('Error: Action type mismatch. Please refresh the page.');
        return;
      }

      console.log('VPSelectionModal - Action dispatched successfully, waiting for saga...');
    } catch (error) {
      console.error('VPSelectionModal - Error dispatching action:', error);
      setIsProcessing(false);
      alert(`Error: ${error.message || 'Failed to dispatch action'}`);
    }
  };

  // Log when updatingVPKeepStatus changes (indicates reducer received the action)
  useEffect(() => {
    console.log('VPSelectionModal - updatingVPKeepStatus changed:', {
      updatingVPKeepStatus,
      updateVPKeepStatusError,
      isProcessing,
      timestamp: new Date().toISOString(),
    });
  }, [updatingVPKeepStatus, updateVPKeepStatusError, isProcessing]);

  // Handle bulk update success or error
  useEffect(() => {
    console.log('VPSelectionModal - useEffect triggered:', {
      updatingVPKeepStatus,
      updateVPKeepStatusError,
      isProcessing,
      shouldProceed: !updatingVPKeepStatus && !updateVPKeepStatusError && isProcessing,
    });

    if (isProcessing) {
      if (updateVPKeepStatusError) {
        console.error('VPSelectionModal - Update failed:', updateVPKeepStatusError);
        setIsProcessing(false);
        // Don't proceed with downgrade if update failed
        alert(`Failed to update VP status: ${updateVPKeepStatusError}`);
        return;
      }

      // Check if update completed successfully
      // updatingVPKeepStatus should be false after success
      if (!updatingVPKeepStatus && !updateVPKeepStatusError) {
        console.log('VPSelectionModal - Update successful, proceeding with downgrade');
        setIsProcessing(false);
        onConfirm();
      } else if (updatingVPKeepStatus) {
        console.log('VPSelectionModal - Still updating, waiting...');
      } else {
        // If isProcessing is true but updatingVPKeepStatus is false and no error,
        // it means the action might not have been dispatched or reducer didn't receive it
        console.warn(
          'VPSelectionModal - isProcessing is true but updatingVPKeepStatus is false - action may not have been dispatched'
        );
      }
    }
  }, [updatingVPKeepStatus, updateVPKeepStatusError, isProcessing, onConfirm]);

  // Safety timeout: if processing takes more than 30 seconds, reset and show error
  useEffect(() => {
    if (isProcessing && updatingVPKeepStatus) {
      const timeoutId = setTimeout(() => {
        console.error('VPSelectionModal - Update timeout: Process took more than 30 seconds');
        setIsProcessing(false);
        alert('The update is taking longer than expected. Please try again or refresh the page.');
      }, 30000); // 30 seconds timeout

      return () => clearTimeout(timeoutId);
    }
  }, [isProcessing, updatingVPKeepStatus]);

  const selectedCount = Object.values(selectedVPs).filter(Boolean).length;
  const totalVPs = userOwnedVPs?.length || 0;

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('VPSelectionModal - Redux state:', {
        userOwnedVPs,
        userOwnedVPsLoading,
        userOwnedVPsError,
        totalVPs,
        preparingForDowngrade,
        prepareForDowngradeError,
      });
    }
  }, [
    isOpen,
    userOwnedVPs,
    userOwnedVPsLoading,
    userOwnedVPsError,
    totalVPs,
    preparingForDowngrade,
    prepareForDowngradeError,
  ]);

  if ((!loadVpsDirectly && preparingForDowngrade) || userOwnedVPsLoading || !vpLimit) {    return (
      <Modal isOpen={isOpen} toggle={toggleModal} size="lg" backdrop={isPolicyCapDown ? 'static' : true}>
        <ModalHeader toggle={toggleModal}>
          {isPolicyCapDown
            ? 'Select Value Propositions to Keep'
            : 'Select Value Propositions to Keep'}
        </ModalHeader>
        <ModalBody>
          <LoaderWidget />
        </ModalBody>
      </Modal>
    );
  }

  const planName =
    newPlanId === Plan.FREE ? 'Free' : newPlanId === Plan.PREMIUM ? 'Premium' : 'Executive';

  return (
    <Modal isOpen={isOpen} toggle={toggleModal} size="lg" backdrop={isPolicyCapDown ? 'static' : true}>
      <ModalHeader toggle={toggleModal}>Select Value Propositions to Keep</ModalHeader>
      <ModalBody>
        {prepareForDowngradeError && (
          <Alert color="danger" className="mb-3">
            Error preparing for downgrade: {prepareForDowngradeError}
          </Alert>
        )}
        {userOwnedVPsError && (
          <Alert color="danger" className="mb-3">
            Error loading Value Propositions: {userOwnedVPsError}
          </Alert>
        )}
        {updateVPKeepStatusError && (
          <Alert color="danger" className="mb-3">
            Error updating Value Propositions: {updateVPKeepStatusError}
          </Alert>
        )}

        <div className="mb-3">
          {isPolicyCapDown ? (
            <>
              <p>
                Our subscription policy for the <strong>{planName}</strong> plan has changed.
              </p>
              <p>
                Your plan now allows up to <strong>{vpLimit} Value Propositions</strong>. You
                currently have <strong>{totalVPs}</strong>.
              </p>
              <p className="text-muted">
                Select which {vpLimit} Value Propositions you want to keep accessible. Unchecked VPs
                will be marked as limited.
              </p>
            </>
          ) : (
            <>
              <p>
                You are {isUpgrade ? 'upgrading to' : 'downgrading to'}{' '}
                <strong>{planName}</strong> plan.
              </p>
              <p>
                Your new plan allows up to <strong>{vpLimit} Value Propositions</strong>.
              </p>
              <p className="text-muted">
                {isUpgrade
                  ? `You currently have ${totalVPs} Value Propositions, but your new plan only allows ${vpLimit}. Select which ${vpLimit} Value Propositions you want to keep accessible. Uncheck any VPs you don't need.`
                  : `Select which Value Propositions you want to keep accessible after the downgrade. Uncheck any VPs you don't need. You can select up to ${vpLimit} VPs.`}
              </p>
            </>
          )}
        </div>

        {totalVPs === 0 ? (
          <Alert color="info">You don't have any user-owned Value Propositions to manage.</Alert>
        ) : (
          <>
            <div className="mb-3">
              <strong>
                Selected: {selectedCount} / {totalVPs} VPs
                {selectedCount > vpLimit && (
                  <span className="text-danger ml-2">
                    (Warning: You've selected {selectedCount} VPs, but your new plan only allows{' '}
                    {vpLimit})
                  </span>
                )}
              </strong>
            </div>

            {/* Select All / Deselect All / Select Latest checkbox and buttons */}
            <div className="mb-3 pb-2 border-bottom">
              <div className="d-flex align-items-center flex-wrap gap-3">
                <FormGroup check className="mb-0">
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={
                        userOwnedVPs && userOwnedVPs.length > 0
                          ? userOwnedVPs.every((vp) => {
                              const vpId = vp.vpId || vp.VpId;
                              return selectedVPs[vpId] === true;
                            })
                          : false
                      }
                      onChange={handleSelectAll}
                      disabled={isProcessing || updatingVPKeepStatus || totalVPs === 0}
                    />
                    <span className="ml-2 font-weight-bold">
                      {userOwnedVPs && userOwnedVPs.length > 0
                        ? userOwnedVPs.every((vp) => {
                            const vpId = vp.vpId || vp.VpId;
                            return selectedVPs[vpId] === true;
                          })
                          ? 'Deselect All'
                          : 'Select All'
                        : 'Select All'}
                    </span>
                  </Label>
                </FormGroup>
                {totalVPs > vpLimit && (
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={handleSelectLatest}
                    disabled={isProcessing || updatingVPKeepStatus || totalVPs === 0}
                    className="ml-2"
                  >
                    Select Latest {vpLimit} VPs
                  </Button>
                )}
                <Dropdown
                  label="Sort by"
                  width={220}
                  className="ml-auto mb-0"
                  value={orderedByOption}
                  options={Object.values(SortingOptions)}
                  onClick={(option) => setOrderedByOption(option)}
                />
              </div>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {userOwnedVPs
                ?.slice() // Create a copy to avoid mutating the original array
                .sort((a, b) => sortOwnedVps(a, b, orderedByOption))
                .map((vp) => {
                  // Handle both PascalCase (from API) and camelCase
                  const vpId = vp.vpId || vp.VpId;
                  const vpName = vp.vpName || vp.VpName || 'Unnamed VP';
                  const fundamentalsCount = vp.fundamentalsCount || vp.FundamentalsCount || 0;
                  const updateDate = vp.updateDate || vp.UpdateDate;
                  const clientName = vp.clientName || vp.ClientName || 'Unknown Client';
                  const implementation = getVpImplementation(vp);
                  const currencyIndex = vp.currency ?? vp.Currency;
                  const exchangeRate =
                    currencyIndex != null && currencies ? currencies[currencyIndex] : 1;
                  const currencySymbol = getCurrencyByIndex(currencyIndex)?.symbol ?? '$';
                  const deliveredLabel =
                    implementation != null
                      ? `${currencySymbol} ${formatMoney(implementation * exchangeRate)}`
                      : null;

                  return (
                    <FormGroup
                      key={vpId}
                      check
                      className="mb-2 border rounded"
                      style={{ padding: '0.75rem 0.75rem 0.75rem 2rem' }}
                    >
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={selectedVPs[vpId] === true}
                          onChange={() => handleToggleVP(vpId)}
                          disabled={isProcessing || updatingVPKeepStatus}
                        />
                        <div className="ml-2">
                          <strong>{vpName}</strong>
                          <div className="text-muted small">
                            {clientName}
                          </div>
                          <div className="text-muted small">
                            {fundamentalsCount} fundamentals
                            {deliveredLabel && ` • Delivered Value: ${deliveredLabel}`}
                            {updateDate && ` • Updated: ${new Date(updateDate).toLocaleDateString()}`}
                          </div>
                        </div>
                      </Label>
                    </FormGroup>
                  );
                })}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={
            isProcessing ||
            updatingVPKeepStatus ||
            (!loadVpsDirectly && preparingForDowngrade) ||
            userOwnedVPsLoading ||
            selectedCount > vpLimit
          }
        >
          {updatingVPKeepStatus
            ? 'Updating...'
            : isPolicyCapDown
            ? 'Confirm selection'
            : isUpgrade
            ? 'Confirm & Continue Upgrade'
            : 'Confirm & Continue Downgrade'}
        </Button>{' '}
        <Button
          color="secondary"
          onClick={toggleModal}
          disabled={isProcessing || updatingVPKeepStatus}
        >
          {isPolicyCapDown ? 'Maybe later' : 'Cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  subscriptionRulesByPlan: state.User?.subscriptionRulesByPlan,
  userOwnedVPs: state.ValueProposition?.userOwnedVPs,
  userOwnedVPsLoading: state.ValueProposition?.userOwnedVPsLoading,
  userOwnedVPsError: state.ValueProposition?.userOwnedVPsError,
  preparingForDowngrade: state.ValueProposition?.preparingForDowngrade,
  prepareForDowngradeError: state.ValueProposition?.prepareForDowngradeError,
  updatingVPKeepStatus: state.ValueProposition?.updatingVPKeepStatus,
  updateVPKeepStatusError: state.ValueProposition?.updateVPKeepStatusError,
  currencies: state.App?.currencies,
});

const mapDispatchToProps = (dispatch) => ({
  prepareForDowngrade: () => dispatch(prepareForDowngrade()),
  getUserOwnedVPs: () => dispatch(getUserOwnedVPs()),
  getCurrencies: () => dispatch(getCurrencies()),
  bulkUpdateVPKeepStatus: (payload) => {
    console.log('[mapDispatchToProps] Dispatching bulkUpdateVPKeepStatus with payload:', payload);
    const action = bulkUpdateVPKeepStatus(payload.vps);
    console.log('[mapDispatchToProps] Action created:', action);
    const result = dispatch(action);
    console.log('[mapDispatchToProps] Dispatch result:', result);
    return result;
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(VPSelectionModal);
